import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import * as XLSX from 'xlsx-js-style';
import { BarLoader } from 'react-spinners';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { PiDownloadSimple } from 'react-icons/pi';
import { BsFiletypeCsv } from 'react-icons/bs';
import { LiaFileExcel } from 'react-icons/lia';
import { FaFileCircleExclamation } from 'react-icons/fa6';
import { RiRefreshLine } from 'react-icons/ri';
import { toast } from 'react-toastify';
import ApiService from '../../services/apiService';
import Pagination from '../pagination/pagination';
import './reports.css';

export const DATE_API = 'YYYY-MM-DD';
export const PAGE_SIZES = [20, 50, 100];
export const DEFAULT_PAGE_SIZE = 50;

export const APPROVAL_OPTIONS = [
    { value: '', label: 'All' },
    { value: '1', label: 'Approved' },
    { value: '0', label: 'Pending' },
];
export const DELIVERY_OPTIONS = [
    { value: '', label: 'All' },
    { value: 'Not Delivered', label: 'Not Delivered' },
    { value: 'Partially Delivered', label: 'Partially Delivered' },
    { value: 'Completely Delivered', label: 'Completely Delivered' },
];

const emptyFilters = () => ({
    poNo: '',
    item: null,
    vendor: null,
    fromDate: moment().format(DATE_API),
    toDate: moment().format(DATE_API),
    property: null,
    unit: null,
    stockType: null,
    approval: APPROVAL_OPTIONS[0],
    delivery: DELIVERY_OPTIONS[0],
});

export const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#F5F5F5' : null,
        color: '#000',
        ':hover': { backgroundColor: '#F5F5F5', color: '#000' },
    }),
};

export const fmtMoney = (v) => (v === null || v === undefined || v === '' ? '' :
    Number(v).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }));
// Calendar dates: take the date part as-is, no timezone conversion.
export const fmtDate = (v) => (v ? moment(String(v).slice(0, 10), DATE_API).format('DD-MMM-YYYY') : '');

const badgeStyle = (bg, color = '#fff') => ({
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, lineHeight: '20px', background: bg, color,
});
const BADGE_RED = badgeStyle('#d9534f');
const BADGE_AMBER = badgeStyle('#f0ad4e', '#000');
const BADGE_GREEN = badgeStyle('#2e9e5b');
const BADGE_GREY = badgeStyle('#8a8a8a');

// Matching ignores case, spacing and spelling variants (e.g. "Aproved", "Fully / Completely Delivered")
export const DeliveryBadge = ({ value }) => {
    if (!value) return null;
    const v = String(value).trim().toLowerCase();
    let style = badgeStyle('#999');
    if (v.includes('not')) style = BADGE_RED;
    else if (v.includes('partial')) style = BADGE_AMBER;
    else if (v.includes('complete') || v.includes('full') || v.includes('delivered')) style = BADGE_GREEN;
    return <span style={style}>{value}</span>;
};
export const ApprovalBadge = ({ value }) => {
    if (!value && value !== 0) return null;
    const v = String(value).trim().toLowerCase();
    if (v === '1' || v.startsWith('app') || v === 'aproved') return <span style={BADGE_GREEN}>Approved</span>;
    if (v === '0' || v.startsWith('pend')) return <span style={BADGE_GREY}>Pending</span>;
    if (v.startsWith('hold')) return <span style={BADGE_AMBER}>{value}</span>;
    if (v.startsWith('rej') || v.startsWith('cancel')) return <span style={BADGE_RED}>{value}</span>;
    return <span style={BADGE_GREY}>{value}</span>;
};

// Columns in display order. `money` = 3 decimals right-aligned, `qty` = plain right-aligned number.
const COLS = [
    { header: 'PO No', field: 'lpoNo', width: 130 },
    { header: 'PO Date', field: 'lpoDate', width: 120, date: true },
    { header: 'Supplier', field: 'supplier', width: 220 },
    { header: 'Property', field: 'propertyName', width: 180 },
    { header: 'Unit', field: 'unitNo', width: 110 },
    { header: 'Nature of Job', field: 'natureofJob', width: 160 },
    { header: 'Work Order No', field: 'workOrderNo', width: 140 },
    { header: 'Requisition No', field: 'requisitionNo', width: 140 },
    { header: 'Req Type', field: 'reqType', width: 120 },
    { header: 'Item Description', field: 'itemDescription', width: 300 },
    { header: 'Specification', field: 'specification', width: 240 },
    { header: 'Qty', field: 'qty', width: 100, qty: true },
    { header: 'Unit Price', field: 'unitPrice', width: 130, money: true },
    { header: 'Gross', field: 'grossAmount', width: 130, money: true },
    { header: 'Discount', field: 'discountAmount', width: 130, money: true },
    { header: 'Net', field: 'netAmount', width: 130, money: true },
    { header: 'VAT', field: 'vatAmount', width: 120, money: true },
    { header: 'Total', field: 'totalAmount', width: 140, money: true },
    { header: 'Prepared By', field: 'preparedBy', width: 140 },
    { header: 'Reviewed By', field: 'reviewedBy', width: 140 },
    { header: 'Approved By', field: 'approvedBy', width: 140 },
    { header: 'Approval Status', field: 'approvalStatus', width: 150, approval: true },
    { header: 'Delivery Status', field: 'deliveryStatus', width: 180, delivery: true },
];

const cellText = (row, c) => {
    const v = row[c.field];
    if (c.money) return fmtMoney(v);
    if (c.date) return fmtDate(v);
    return v ?? '';
};

// ---- API client -------------------------------------------------------------------------
// Empty values are omitted so the backend treats them as "no filter".
const fetchPurchaseOrderAdvanced = async (apiService, filters, pageNo, pageSize) => {
    const params = new URLSearchParams();
    const add = (key, value) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
            params.append(`inventoryPOReportRequest.${key}`, String(value).trim());
        }
    };
    add('POrderNo', filters.poNo);
    add('ItemNo', filters.item?.value?.itemNo);
    add('ItemDescription', filters.item?.value?.itemDescription);
    add('VendorName', filters.vendor?.label);
    add('FromDate', filters.fromDate);
    add('ToDate', filters.toDate);
    add('Status', filters.approval?.value);
    add('Filter', filters.delivery?.value);
    add('CostCenter1', filters.property?.label);
    add('CostCenter2', filters.unit?.label);
    add('StockType', filters.stockType?.value);
    if (pageSize) {
        params.append('PageNo', pageNo || 1);
        params.append('PageSize', pageSize);
    }
    const res = await apiService.getApiParamater('GET_PO_ADVANCED_REPORT', `?${params.toString()}`);
    const body = res?.data;
    // 204 = no rows, not an error
    if (!body || body.statusCode === 204) return { rows: [], pagination: null };
    if (body.statusCode && body.statusCode >= 400) throw new Error(body.message || 'Request failed');
    const list = body.data ?? body.result;
    return { rows: Array.isArray(list) ? list : [], pagination: body.paginationData || null };
};

// ---- Export -----------------------------------------------------------------------------
const REPORT_TITLE = 'Purchase Order Details List';
export const HEADER_FILL = 'FF6E5199'; // same purple as the grid header

// Row 1 = title, row 2 = column headers, then data and a Total row. Styles apply to Excel only (CSV is plain text).
const NUM_FMT = '#,##0.000';
const SUM_FIELDS = ['qty', 'grossAmount', 'discountAmount', 'netAmount', 'vatAmount', 'totalAmount'];
export const exportRows = (rows, type, cfg = {}) => {
    const { cols: COLS_ = COLS, sumFields = SUM_FIELDS, title = REPORT_TITLE, sheet = 'Purchase Order Details', file = 'Purchase_Order_Details' } = cfg;
    const body = rows.map((r) => COLS_.map((c) => {
        if (c.money || c.qty) return r[c.field] === null || r[c.field] === undefined ? '' : Number(r[c.field]);
        return cellText(r, c);
    }));
    const totals = COLS_.map((c, ci) => {
        if (sumFields.includes(c.field)) return rows.reduce((sum, r) => sum + (Number(r[c.field]) || 0), 0);
        return ci === 0 ? 'Total' : '';
    });
    const ws = XLSX.utils.aoa_to_sheet([[title], COLS_.map((c) => c.header), ...body, totals]);
    ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: COLS_.length - 1 } }];
    ws['A1'].s = { font: { bold: true, sz: 14 }, alignment: { horizontal: 'left' } };
    const totalRow = rows.length + 2;
    COLS_.forEach((c, ci) => {
        const numeric = c.money || c.qty;
        const h = ws[XLSX.utils.encode_cell({ r: 1, c: ci })];
        h.s = {
            font: { bold: true, color: { rgb: 'FFFFFFFF' } },
            fill: { patternType: 'solid', fgColor: { rgb: HEADER_FILL } },
            alignment: { horizontal: numeric ? 'right' : 'left', vertical: 'center' },
        };
        // Numeric columns use a Number format with 3 decimals (data rows and the Total row)
        for (let r = 2; r <= totalRow; r++) {
            const cell = ws[XLSX.utils.encode_cell({ r, c: ci })];
            if (cell && numeric && cell.t === 'n') cell.z = NUM_FMT;
            if (cell && r === totalRow) {
                cell.s = { font: { bold: true }, fill: { patternType: 'solid', fgColor: { rgb: 'FFE5E0EE' } }, alignment: { horizontal: numeric ? 'right' : 'left' } };
            }
        }
    });
    // Auto-fit: widest of header, formatted values and the total
    ws['!cols'] = COLS_.map((c, ci) => {
        let max = c.header.length + 2; // bold header
        rows.forEach((r) => { max = Math.max(max, String(cellText(r, c)).length); });
        if (sumFields.includes(c.field)) max = Math.max(max, fmtMoney(totals[ci]).length);
        return { wch: Math.max(max + 3, 10) };
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheet);
    XLSX.writeFile(wb, `${file}.${type}`, type === 'csv' ? { bookType: 'csv' } : undefined);
};

// ---- Filter bar -------------------------------------------------------------------------
export const TextField = ({ label, value, onChange, placeholder }) => (
    <div className="col-md-3 col-sm-6 mb-2">
        <label className="form-label mb-1 small">{label}</label>
        <input type="text" className="form-Input w-100" value={value} placeholder={placeholder || label}
            onChange={(e) => onChange(e.target.value)} />
    </div>
);

export const SelectField = ({ label, value, onChange, options, isClearable, placeholder, isDisabled }) => (
    <div className="col-md-3 col-sm-6 mb-2">
        <label className="form-label mb-1 small">{label}</label>
        <Select className="p-0" menuPortalTarget={document.body} styles={selectStyles} value={value}
            onChange={onChange} options={options} isClearable={isClearable} isDisabled={isDisabled}
            placeholder={placeholder || 'Select...'} />
    </div>
);

export const toDateObj = (s) => (s ? moment(s, DATE_API).toDate() : null);

const POFilterBar = ({ apiService, stockTypes, suppliers, properties, onApply, onReset }) => {
    const [f, setF] = useState(emptyFilters());
    const [units, setUnits] = useState([]);
    const set = (key) => (value) => setF((p) => ({ ...p, [key]: value }));
    const dateError = f.toDate && !f.fromDate ? 'Pick a From date — a To date on its own is ignored.' : '';

    const toOptions = (list, labelKey) => (list || []).map((x) => ({ value: x, label: x?.[labelKey] }));

    // Units depend on the selected property (same cascade as the Purchase Order report)
    const handleProperty = async (opt) => {
        setF((p) => ({ ...p, property: opt, unit: null }));
        setUnits([]);
        if (!opt) return;
        try {
            const res = await apiService.getApiParamater('GET_COST_TWO', `?CostCenter1=${encodeURIComponent(opt.label)}`);
            setUnits(res?.data?.result || []);
        } catch (e) {
            console.error('Error loading units:', e);
        }
    };

    const loadItems = async (input) => {
        try {
            const params = new URLSearchParams();
            params.append('PageNo', 1);
            params.append('PageSize', 20);
            params.append('ItemDescription', input || '');
            const res = await apiService.getApiParamater('GET_ITEM', `?${params.toString()}`);
            return (res?.data?.result || []).map((it) => ({ value: it, label: `${it.itemNo} - ${it.itemDescription}` }));
        } catch (e) {
            console.error('Error loading items:', e);
            return [];
        }
    };

    const apply = () => {
        if (dateError) {
            toast.warning(dateError);
            return;
        }
        onApply(f);
    };
    const reset = () => {
        setF(emptyFilters());
        setUnits([]);
        onReset();
    };

    return (
        <div className="gridBg m-0 mb-3 p-3" style={{ borderRadius: 16 }}>
            <div className="row">
                <TextField label="PO No" value={f.poNo} onChange={set('poNo')} />
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">Item</label>
                    <AsyncSelect cacheOptions defaultOptions loadOptions={loadItems} isClearable className="p-0"
                        menuPortalTarget={document.body} styles={selectStyles} value={f.item}
                        placeholder="Item No / Description" onChange={set('item')} />
                </div>
                <SelectField label="Supplier" value={f.vendor} onChange={set('vendor')} isClearable placeholder="All"
                    options={toOptions(suppliers, 'supplierName')} />
                <SelectField label="Stock Type" value={f.stockType} onChange={set('stockType')} isClearable placeholder="All"
                    options={(stockTypes || []).map((s) => ({ value: s.stockTypeCode, label: s.stockTypeName }))} />
                <SelectField label="Property" value={f.property} onChange={handleProperty} isClearable placeholder="All"
                    options={toOptions(properties, 'description')} />
                <SelectField label="Unit" value={f.unit} onChange={set('unit')} isClearable placeholder="All"
                    isDisabled={!f.property} options={toOptions(units, 'description')} />
                <SelectField label="Approval Status" value={f.approval} onChange={(o) => set('approval')(o || APPROVAL_OPTIONS[0])} options={APPROVAL_OPTIONS} />
                <SelectField label="Delivery Status" value={f.delivery} onChange={(o) => set('delivery')(o || DELIVERY_OPTIONS[0])} options={DELIVERY_OPTIONS} />
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">From Date</label>
                    <DatePicker selected={toDateObj(f.fromDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="From"
                        onChange={(d) => set('fromDate')(d ? moment(d).format(DATE_API) : '')} />
                </div>
                <div className="col-md-3 col-sm-6 mb-2">
                    <label className="form-label mb-1 small">To Date</label>
                    <DatePicker selected={toDateObj(f.toDate)} dateFormat="dd-MMM-yyyy" isClearable wrapperClassName="w-100"
                        className="form-Input w-100" placeholderText="To" minDate={toDateObj(f.fromDate)}
                        onChange={(d) => set('toDate')(d ? moment(d).format(DATE_API) : '')} />
                    {dateError && <div className="small text-danger">{dateError}</div>}
                </div>
                <div className="col-md-3 col-sm-6 mb-2 d-flex align-items-end gap-2">
                    <div className="mainbtnGry flex-end gap-2 w-50" role="button" tabIndex={0} onClick={apply}
                        onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}>
                        <FaFileCircleExclamation color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Apply</div>
                    </div>
                    <div className="mainbtnRed flex-end gap-2 w-50" role="button" tabIndex={0} onClick={reset}
                        onKeyDown={(e) => { if (e.key === 'Enter') reset(); }}>
                        <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                        <div className="center">Reset</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---- Grid -------------------------------------------------------------------------------
const POGrid = ({ rows }) => {
    const isFirstOfPO = useCallback((params) => {
        if (params.node.rowIndex === 0) return true;
        const prev = params.api.getDisplayedRowAtIndex(params.node.rowIndex - 1);
        return prev?.data?.lpoNo !== params.data?.lpoNo;
    }, []);

    const colDefs = useMemo(() => COLS.map((c, i) => {
        const def = {
            headerName: c.header,
            field: c.field,
            width: c.width,
            suppressMovable: true,
            filter: true,
            floatingFilter: true,
            ...(i === 0 ? { pinned: 'left' } : {}),
            ...(c.money || c.qty ? { type: 'rightAligned', headerClass: 'ag-right-aligned-header' } : {}),
        };
        if (i === 0) {
            // Repeat the PO No in muted text under its first line so groups read as one PO.
            def.cellRenderer = (p) => (isFirstOfPO(p)
                ? <strong>{p.value}</strong>
                : <span style={{ color: '#aaa' }}>{p.value}</span>);
        } else if (c.money) {
            def.valueFormatter = (p) => fmtMoney(p.value);
        } else if (c.date) {
            def.valueFormatter = (p) => fmtDate(p.value);
        } else if (c.delivery) {
            def.cellRenderer = (p) => <DeliveryBadge value={p.value} />;
        } else if (c.approval) {
            def.cellRenderer = (p) => <ApprovalBadge value={p.value} />;
        }
        return def;
    }), [isFirstOfPO]);

    const getRowStyle = useCallback((p) => {
        const style = { background: p.node.rowIndex % 2 === 0 ? '#F0EDF5' : '#fff' };
        if (isFirstOfPO(p) && p.node.rowIndex > 0) style.borderTop = '2px solid #6E5199';
        return style;
    }, [isFirstOfPO]);

    return (
        <div className="ag-theme-quartz" style={{ height: 560 }}>
            <AgGridReact rowData={rows} columnDefs={colDefs} getRowStyle={getRowStyle} suppressMovableColumns />
        </div>
    );
};

// ---- Screen -----------------------------------------------------------------------------
const PurchaseOrderAdvanced = () => {
    const apiService = useRef(new ApiService()).current;
    const reqId = useRef(0);
    const [filters, setFilters] = useState(emptyFilters());
    const [pageNo, setPageNo] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stockTypes, setStockTypes] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [properties, setProperties] = useState([]);
    const [exportOpen, setExportOpen] = useState(false);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        apiService.getApiParamater('GET_STOCK_TYPE', '?isActive=true')
            .then((res) => setStockTypes(res?.data?.result || []))
            .catch((e) => console.error('Error loading stock types:', e));
        apiService.get('GET_SUPPLIER_KANOO')
            .then((res) => setSuppliers(res?.data?.result || []))
            .catch((e) => console.error('Error loading suppliers:', e));
        apiService.get('GET_COST_ONE')
            .then((res) => setProperties(res?.data?.result || []))
            .catch((e) => console.error('Error loading properties:', e));
    }, [apiService]);

    // Fetches on mount, Apply and page / page-size change only. Stale responses are dropped.
    useEffect(() => {
        const id = ++reqId.current;
        setLoading(true);
        setError(null);
        fetchPurchaseOrderAdvanced(apiService, filters, pageNo, pageSize)
            .then(({ rows: r, pagination: p }) => {
                if (id !== reqId.current) return;
                setRows(r);
                setPagination(p);
            })
            .catch((e) => {
                if (id !== reqId.current) return;
                setRows([]);
                setPagination(null);
                setError(e?.message || 'Unable to load the report.');
            })
            .finally(() => { if (id === reqId.current) setLoading(false); });
    }, [apiService, filters, pageNo, pageSize]);

    const handleApply = (f) => { setFilters({ ...f }); setPageNo(1); };
    const handleReset = () => { setFilters(emptyFilters()); setPageNo(1); };
    // Export covers every matching row: one request with PageNo 1 and PageSize = total records.
    const handleExport = async (type) => {
        const total = pagination?.totalRecords || rows.length;
        if (!total) return;
        setExporting(true);
        try {
            const { rows: all } = await fetchPurchaseOrderAdvanced(apiService, filters, 1, total);
            exportRows(all, type);
        } catch (e) {
            toast.error(e?.message || 'Unable to export the report.');
        } finally {
            setExporting(false);
        }
    };
    const handlePageSize = (e) => { setPageSize(Number(e.target.value)); setPageNo(1); };

    return (
        <div className="p-4">
            <POFilterBar apiService={apiService} stockTypes={stockTypes} suppliers={suppliers} properties={properties} onApply={handleApply} onReset={handleReset} />

            <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                <div className="mainInvHead center">Purchase Order Details</div>
                <div className="ms-auto">
                    <select className="form-select form-select-sm" aria-label="Page size" value={pageSize} onChange={handlePageSize}>
                        {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
                    </select>
                </div>
                <div className="mainbtnGry flex-end gap-2">
                    <Dropdown isOpen={exportOpen} toggle={() => setExportOpen(!exportOpen)}>
                        <DropdownToggle tag="span" aria-expanded={exportOpen} role="button" aria-label="Export">
                            <PiDownloadSimple size={25} />
                        </DropdownToggle>
                        <DropdownMenu className="mt-3">
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('csv')} className="gap-3 d-flex center"><BsFiletypeCsv /><div>Export CSV</div></DropdownItem>
                            <DropdownItem disabled={!rows.length || exporting} onClick={() => handleExport('xlsx')} className="gap-3 d-flex center"><LiaFileExcel /><div>Export Excel</div></DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>

            <div className="gridshad">
                {loading ? (
                    <div className="center p-5"><BarLoader color="#6E5199" height={3} width={200} /></div>
                ) : error ? (
                    <div className="text-danger p-4" role="alert">{error}</div>
                ) : !rows.length ? (
                    <div className="text-muted p-4">No purchase orders found.</div>
                ) : (
                    <POGrid rows={rows} />
                )}
                {pagination && <Pagination pagination={pagination} getItems={(p) => setPageNo(p)} />}
            </div>
        </div>
    );
};

export default PurchaseOrderAdvanced;
